import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Menu, PanelLeftOpen } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Sidebar } from "./Sidebar";
import { HostedLoopInspector } from "./HostedLoopInspector";
import { SearchCommand } from "./SearchCommand";
import { Wordmark } from "./Wordmark";
import { MobileBrandPopover } from "./MobileBrandPopover";

import { useInspector } from "./inspector-context";
import { store, useAppState } from "@/lib/looseend/store";
import { useHostedChatActions } from "@/lib/chat/product/hostedChat";

/** True when the viewport is wide enough for the docked inspector column. */
function useWideViewport() {
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1280px)");
    const onChange = () => setWide(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return wide;
}

export function AppShell({ children }: { children: ReactNode }) {
  const state = useAppState();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { loopId, close } = useInspector();
  const chat = useHostedChatActions();
  const [mobileNav, setMobileNav] = useState(false);
  const wide = useWideViewport();

  const isGuest = state.user?.isGuest === true;
  const name = state.user?.name || "You";
  const collapsed = state.sidebarCollapsed;

  /**
   * WP-B closure — the visible "New conversation" action is a hosted command.
   * The conversation id used for routing is the one the backend assigned; no
   * local conversation record is created, so the route can never point at a
   * conversation that does not durably exist.
   */
  const newConversation = () => {
    void (async () => {
      const created = await chat.startConversation();
      if (!created.ok) {
        toast.error(created.message);
        return;
      }
      await navigate({
        to: "/app/chat/$conversationId",
        params: { conversationId: created.value },
      });
    })();
  };

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-background">
      <SearchCommand />

      {collapsed ? null : (
        <aside className="hidden w-[280px] shrink-0 border-r border-border md:block">
          <Sidebar
            onNewConversation={newConversation}
            onCollapse={() => store.setSidebarCollapsed(true)}
          />
        </aside>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-2 md:hidden">
          <Sheet open={mobileNav} onOpenChange={setMobileNav}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open navigation">
                <Menu className="size-5" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[288px] p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <Sidebar onNavigate={() => setMobileNav(false)} onNewConversation={newConversation} />
            </SheetContent>
          </Sheet>

          <MobileBrandPopover />

          {isGuest ? (
            <Button variant="outline" size="sm" className="ml-auto h-8 shrink-0" asChild>
              <Link to="/auth" search={{ redirect: pathname }} data-testid="header-sign-in">
                Sign in
              </Link>
            </Button>
          ) : (
            <Link
              to="/app/settings"
              aria-label="Settings"
              className="ml-auto grid size-8 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-semibold text-primary"
            >
              {name.slice(0, 1).toUpperCase()}
            </Link>
          )}
        </header>

        {collapsed ? (
          <div className="hidden shrink-0 items-center gap-2 px-3 pt-3 md:flex">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Expand sidebar"
              onClick={() => store.setSidebarCollapsed(false)}
            >
              <PanelLeftOpen className="size-[18px]" aria-hidden="true" />
            </Button>
            <Wordmark className="text-[15px]" />
            {isGuest ? (
              <Button variant="outline" size="sm" className="ml-auto h-8" asChild>
                <Link to="/auth" search={{ redirect: pathname }}>
                  Sign in
                </Link>
              </Button>
            ) : null}
          </div>
        ) : null}

        <main className="min-h-0 flex-1 overflow-hidden">{children}</main>
      </div>

      {wide && loopId ? (
        <aside className="hidden w-[380px] shrink-0 border-l border-border xl:block">
          <HostedLoopInspector loopId={loopId} onClose={close} />
        </aside>
      ) : null}

      <Sheet open={!wide && Boolean(loopId)} onOpenChange={(open) => !open && close()}>
        <SheetContent side="bottom" className="h-[85dvh] gap-0 rounded-t-[14px] p-0">
          <SheetTitle className="sr-only">Loop details</SheetTitle>
          {loopId ? <HostedLoopInspector loopId={loopId} variant="sheet" onClose={close} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
